"use client";

import { useEffect, useState, useMemo, useRef } from "react";
import styles from "../css/inventoryManagement.module.css";
import Navbar from "../../../components/navbar";
import Footer from "../../../components/footer";
import StatsCard from "../../../components/stats-card";
import SearchBar from "../../../components/SearchBar";
import Dropdown from "../../../components/Dropdown";
import Table from "../../../components/Table";
import Pagination from "../../../components/Pagination";
import Button from "../../../components/Button";
import Status from "../../../components/Status";
import { useNavigate } from "react-router-dom";
import FilterButtons from "../../../components/universal-filter-tabs.jsx";
import { useAuth } from "../../../context/AuthContext";
import { fetchDepartments, fetchInventoryByDepartment } from "../../../api/Inventory/ProductCatalogueApi.js";
import { getExpiredStockKpi, getLowStockKpi, getTotalInventory, getTotalInventoryValue } from "../../../api/analytics/analyticsApi";

export default function InventoryManagement() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const roleId = Number(user?.roleid);
  const [departments, setDepartments] = useState([]);

  // Helper: format yyyy-mm-dd to dd/mm/yyyy for UI display only
  const formatDateDMY = (yyyyMmDd) => {
    const s = String(yyyyMmDd || '').trim();
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return s;
    return `${m[3]}/${m[2]}/${m[1]}`;
  };
  const [activeFilter, setActiveFilter] = useState("");
  const [rows, setRows] = useState([]);
  const [titleFilter, setTitleFilter] = useState("");
  const [selectFilter, setSelectFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [hasLoadedFilters, setHasLoadedFilters] = useState(false);
  const prevActiveFilterRef = useRef("");
  const pageSize = 5;
  const [totalProducts, setTotalProducts] = useState(null);
  const [totalProductsError, setTotalProductsError] = useState(null);
  const [lowStockKpiState, setLowStockKpiState] = useState({ totalLowStock: null });
  const [lowStockError, setLowStockError] = useState(null);
  const [expiringKpi, setExpiringKpi] = useState({ totalExpiredStock: null });
  const [expiringError, setExpiringError] = useState(null);
  const [inventoryValue, setInventoryValue] = useState(null);
  const [inventoryValueError, setInventoryValueError] = useState(null);
  const orderedDepartments = useMemo(() => {
    return [...departments].sort((a, b) => Number(a.sectionid) - Number(b.sectionid));
  }, [departments]);

  const columns = (() => {
    const commonStatus = {
      header: "Status",
      accessor: "status",
      render: (value) => (<Status status={value} variant="stock" />),
    };
    const commonQty = {
      header: "Qty",
      accessor: "qty",
      render: (value) => {
        const num = Number(value);
        if (!Number.isFinite(num)) return value ?? "";
        return Math.trunc(num);
      },
    };
    const commonExpiry = {
    header: "Expiry Date",
    accessor: "expiry",
    render: (value) => formatDateDMY(value),
  };
    const commonReorder = { header: "Reorder Level", accessor: "reorder" };
    const commonLastReorder = {
    header: "Last Reorder",
    accessor: "lastReorder",
    render: (value) => formatDateDMY(value),
  };
    const commonAction = {
      header: "Action",
      accessor: "action",
      render: (_, row) => (
        <Button
          variant="primary"
          className={styles.viewBtn}
          onClick={() => {
            let storage;
            if (typeFilter === "Drug") {
              storage = "Dispensary";
            } else if (typeFilter === "Item") {
              storage = "Front Shop";
            } else {
              storage = row.storage || activeFilter;
            }

            const base = `/inventory/item/${row.sku}?storage=${encodeURIComponent(storage)}`;

            let url = base;
            if (typeFilter === "Item") {
              url = `${base}&fromFrontShop=1`;
            } else if (!typeFilter && activeFilter === "Front Shop") {
              url = `${base}&fromFrontShop=1`;
            }

            navigate(url);
          }}
        >
          View More
        </Button>
      ),
    };
    const drugColumns = [
      { header: "Drug Name", accessor: "title" }, { header: "Nappi Code", accessor: "nappi" }, { header: "Active Ingredient", accessor: "ingredient" },
      commonQty, commonExpiry, commonStatus, commonReorder, commonLastReorder, commonAction,
    ];

    const itemColumns = [
      { header: "Item Name", accessor: "title" },
      commonQty, commonExpiry, commonStatus, commonReorder, commonLastReorder, commonAction,
    ];

    const clinicColumns = [
      { header: "Item Name", accessor: "title" }, { header: "Storage", accessor: "storage" },
      commonQty, commonExpiry, commonStatus, commonReorder, commonLastReorder, commonAction,
    ];

    if (typeFilter === "Drug") {
      return drugColumns;
    }
    if (typeFilter === "Item") {
      return itemColumns;
    }

    if (activeFilter === "Clinic") {
      return clinicColumns;
    }
    if (activeFilter === "Front Shop" || activeFilter === "OTC") {
      return itemColumns;
    }
    // Default: Dispensary
    return drugColumns;
  })();

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setTotalProductsError(null);
        const count = await getTotalInventory();
        if (!cancelled) setTotalProducts(count);
      } catch (err) {
        if (!cancelled) setTotalProductsError(err?.message || "Failed to load total products");
      }
    })();

    (async () => {
      try {
        setLowStockError(null);
        const kpi = await getLowStockKpi();
        if (!cancelled) setLowStockKpiState(kpi);
      } catch (err) {
        if (!cancelled) setLowStockError(err?.message || "Failed to load low stock alerts");
      }
    })();

    (async () => {
      try {
        setExpiringError(null);
        const kpi = await getExpiredStockKpi();
        if (!cancelled) setExpiringKpi(kpi);
      } catch (err) {
        if (!cancelled) setExpiringError(err?.message || "Failed to load expiring stock");
      }
    })();

    (async () => {
      try {
        setInventoryValueError(null);
        const value = await getTotalInventoryValue();
        if (!cancelled) setInventoryValue(value);
      } catch (err) {
        if (!cancelled) setInventoryValueError(err?.message || "Failed to load inventory value");
      }
    })();

    return () => { cancelled = true; };
  }, []);

  const filtered = (() => {
    const q = String(search || "").toLowerCase();
    const selectedType = String(typeFilter || "").trim();
    const selectedStatus = String(selectFilter || "").trim();
    const field = String(titleFilter || "").trim();

    // Helper: base filters that should always apply (type + status)
    const passesBaseFilters = (r) => {
      if (selectedType === "Drug") {
        const storage = String(r.storage || "").toLowerCase();
        if (storage !== "dispensary") return false;
      } else if (selectedType === "Item") {
        const storage = String(r.storage || "").toLowerCase();
        if (storage !== "front shop") return false;
      }

      if (selectedStatus && selectedStatus !== "Select Options") {
        if (String(r.status || "").toLowerCase() !== selectedStatus.toLowerCase()) {
          return false;
        }
      }

      return true;
    };

    // No search text -> just apply base filters and keep original order
    if (!q) {
      return rows.filter((r) => passesBaseFilters(r));
    }

    // When a specific field is chosen, keep existing behavior (no reordering)
    if (field === "Name") {
      return rows.filter((r) => {
        if (!passesBaseFilters(r)) return false;
        return String(r.title || "").toLowerCase().includes(q);
      });
    }

    if (field === "Quantity") {
      return rows.filter((r) => {
        if (!passesBaseFilters(r)) return false;
        return String(r.qty ?? "").toLowerCase().includes(q);
      });
    }

    if (field === "Expiry Date") {
      return rows.filter((r) => {
        if (!passesBaseFilters(r)) return false;
        return String(r.expiry || "").toLowerCase().includes(q);
      });
    }

    // Default search: prioritise name matches, then ingredient, then other fields
    const nameMatches = [];
    const ingredientMatches = [];
    const otherMatches = [];

    rows.forEach((r) => {
      if (!passesBaseFilters(r)) return;

      const name = String(r.title || "").toLowerCase();
      const ingredient = String(r.ingredient || "").toLowerCase();
      const nappi = String(r.nappi || "").toLowerCase();
      const barcode = String(r.barcode || "").toLowerCase();

      const matchesName = name.includes(q);
      const matchesIngredient = ingredient.includes(q);
      const matchesOther = nappi.includes(q) || barcode.includes(q);

      if (!matchesName && !matchesIngredient && !matchesOther) return;

      if (matchesName) {
        nameMatches.push(r);
      } else if (matchesIngredient) {
        ingredientMatches.push(r);
      } else {
        otherMatches.push(r);
      }
    });

    return [...nameMatches, ...ingredientMatches, ...otherMatches];
  })();

  // Reset page on filter/search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter, search, titleFilter, selectFilter, typeFilter]);

  // If Clinic user (role 6), force Clinic view and remove filters
  useEffect(() => {
    if (roleId === 6 && activeFilter === "") {
      setTitleFilter("");
      setSelectFilter("");
      setSearch("");
    }
  }, [roleId, activeFilter]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const deps = await fetchDepartments();
      if (cancelled) return;
      const arr = Array.isArray(deps) ? deps : [];
      setDepartments(arr);

      let saved = null;
      if (typeof window !== "undefined") {
        try {
          const raw = sessionStorage.getItem("inventory.filters.v1");
          saved = raw ? JSON.parse(raw) : null;
        } catch {
          saved = null;
        }
      }

      let nextActive = "";
      if (saved?.activeFilter && arr.some((d) => d.sectionname === saved.activeFilter)) {
        nextActive = saved.activeFilter;
      } else {
        const dep1 = arr.find((d) => Number(d.sectionid) === 1);
        if (dep1) nextActive = dep1.sectionname;
      }

      if (nextActive) setActiveFilter(nextActive);
      if (saved?.typeFilter) setTypeFilter(saved.typeFilter);
      setHasLoadedFilters(true);
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const dep = departments.find((d) => d.sectionname === activeFilter);
    if (!dep) {
      setRows([]);
      return;
    }
    (async () => {
      const data = await fetchInventoryByDepartment(dep.sectionid, typeFilter);
      if (cancelled) return;
      if ((activeFilter || '').toLowerCase().includes('front')) {
        try {
          console.debug('[InventoryManagement] Front Shop raw sample:', Array.isArray(data) ? data.slice(0, 3) : data);
          if (Array.isArray(data) && data[0]) {
            console.debug('[InventoryManagement] Front Shop raw keys of first item:', Object.keys(data[0] || {}));
            if (data[0]?.item) console.debug('[InventoryManagement] Front Shop raw item keys:', Object.keys(data[0].item || {}));
          }
        } catch {}
      }
      const mapped = (data || []).map((r) => {
        // Per Prisma schema and ProductCatalogueController, barcode now lives on inventory
        const barcode = r?.barcode ?? "";
        const shelf = r?.item?.shelvenumber ?? "";
        const quantity = Number(r.quantity ?? 0);
        const reorderLevel = Number(r.reorderlevel ?? 0);
        
        // Calculate status based on quantity and reorder level
        let calculatedStatus = "";
        if (quantity === 0) {
          calculatedStatus = "Out of Stock";
        } else if (quantity < reorderLevel) {
          calculatedStatus = "Low Stock";
        } else {
          calculatedStatus = "In Stock";
        }

        return {
          sku: r.sku,
          title: r.name || "",
          ingredient: r.drug?.activeingredient || "",
          nappi: r.drug?.nappicode || "",
          barcode,
          shelf,
          qty: quantity,
          expiry: r.expiry || "",
          status: calculatedStatus,
          reorder: reorderLevel,
          lastReorder: r.lastReorder || "",
          storage: r.drug ? "Dispensary" : (r.item ? "Front Shop" : ""),
        };
      });
      if ((activeFilter || '').toLowerCase().includes('front')) {
        try {
          console.debug('[InventoryManagement] Front Shop mapped sample:', mapped.slice(0, 3));
        } catch {}
      }
      setRows(mapped);
    })();
    return () => { cancelled = true; };
  }, [activeFilter, departments, typeFilter]);

  useEffect(() => {
    if (!hasLoadedFilters) return;
    if (!activeFilter) return;

    const name = String(activeFilter || "").toLowerCase();
    let defaultType = "";
    if (name.includes("disp") || name.includes("otc")) {
      defaultType = "Drug";
    } else if (name.includes("front") || name.includes("clinic")) {
      defaultType = "Item";
    }

    if (!defaultType) {
      prevActiveFilterRef.current = activeFilter;
      return;
    }

    if (prevActiveFilterRef.current === "") {
      // Initial activation: only apply default when there is no type from session
      if (!typeFilter) {
        setTypeFilter(defaultType);
      }
    } else if (prevActiveFilterRef.current !== activeFilter) {
      // User changed department tab: reset type to the tab-specific default
      setTypeFilter(defaultType);
    }

    prevActiveFilterRef.current = activeFilter;
  }, [activeFilter, typeFilter, hasLoadedFilters]);

  useEffect(() => {
    if (!hasLoadedFilters) return;
    if (typeof window === "undefined") return;
    try {
      const payload = {
        activeFilter,
        typeFilter,
      };
      sessionStorage.setItem("inventory.filters.v1", JSON.stringify(payload));
    } catch {}
  }, [activeFilter, typeFilter, hasLoadedFilters]);

  // Pagination calculations
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage]);
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  const shouldShowTable = Boolean(String(typeFilter || "").trim());

  const totalProductsDisplay =
    typeof totalProducts === "number" ? totalProducts.toString() : "--";

  const lowStockDisplay =
    typeof lowStockKpiState.totalLowStock === "number"
      ? lowStockKpiState.totalLowStock.toString()
      : "--";

  const expiringDisplay =
    typeof expiringKpi.totalExpiredStock === "number"
      ? expiringKpi.totalExpiredStock.toString()
      : "--";

  const inventoryValueDisplay =
    typeof inventoryValue === "number"
      ? `R${inventoryValue.toFixed(2)}`
      : "R0.00";

  return (
    <div className={styles.wrapper}>
      <Navbar />
      <div className={styles.dashboardLayout}>
        <main className={styles.main}>
          <div className={styles.container}>
            <div className={styles.headerRow}>
              <h1 className={styles.title}>Product Catalogue</h1>
            </div>

            <div className={styles.statsRow}>
              <StatsCard title="Total Products" value={totalProductsDisplay} icon="boxes">
                <p className={styles.cardSubtitle}>Number of distinct products in inventory.</p>
                {totalProductsError && (
                  <p className="text-red-500 text-xs mt-1">{totalProductsError}</p>
                )}
              </StatsCard>
              <StatsCard title="Low Stock Alerts" value={lowStockDisplay} icon="alert">
                <p className={styles.cardSubtitle}>Products below their reorder level.</p>
                {lowStockError && (
                  <p className="text-red-500 text-xs mt-1">{lowStockError}</p>
                )}
              </StatsCard>
              <StatsCard title="Expiring Soon" value={expiringDisplay} icon="hourglass">
                <p className={styles.cardSubtitle}>Products expiring within 3 months.</p>
                {expiringError && (
                  <p className="text-red-500 text-xs mt-1">{expiringError}</p>
                )}
              </StatsCard>
              <StatsCard title="Total Inventory Value" value={inventoryValueDisplay} icon="r">
                <p className={styles.cardSubtitle}>Total cost of all items in stock.</p>
                {inventoryValueError && (
                  <p className="text-red-500 text-xs mt-1">{inventoryValueError}</p>
                )}
              </StatsCard>
            </div>
            <div className={styles.filtersBar}>
              <span className={styles.filterLabel}>Search</span>
              <div className={styles.filterInputs}>
                <div className={styles.searchWrap}>
                  <SearchBar
                    value={search}
                    onChange={setSearch}
                    placeholder="Search Product"
                  />
                </div>
              </div>
              <div className={styles.typeFilter}>
                <span className={styles.filterLabel}>Type</span>
                <Dropdown
                  name="typeFilter"
                  options={["Drug", "Item"]}
                  value={typeFilter}
                  onChange={setTypeFilter}
                  placeholder="Select"
                  className={styles.dropdownSmall}
                />
              </div>
              <FilterButtons
                buttons={orderedDepartments.map((d) => d.sectionname)}
                activeButton={activeFilter}
                onButtonClick={setActiveFilter}
                className={styles.segmentTabs}
              />
            </div>

            {shouldShowTable && (
              <>
                {pageData.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-8">
                    No data available at the moment
                  </p>
                ) : (
                  <>
                    <Table columns={columns} data={pageData} />

                    <div className={styles.paginationContainer}>
                      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </main>
      </div>
      <div className={styles.footerWrapper}>
        <Footer />
      </div>
    </div>
  );
}
